import { PaletteOptions } from '@mui/material/styles';
import Color from 'color';
import { appColors } from './appcolors';

/**
 * Sidebar text legibility guard.
 *
 * The drawer background and the drawer text colours are configured
 * independently in Setting → Theme Color, so a saved combination can end up
 * unreadable — the stored dark-mode menu text sits at roughly 1.5:1 on the
 * drawer, which fails WCAG badly. This does not change anyone's configured
 * colour: it only substitutes a legible near-white or near-black when the
 * configured pair would be unreadable, and passes the colour through untouched
 * whenever it already has enough contrast.
 */
const legibleOn = (textColor: string, backgroundColor: string, minimum = 3.5): string => {
  try {
    const background = Color(backgroundColor);
    if (Color(textColor).contrast(background) >= minimum) {
      return textColor;
    }
    return background.isLight() ? '#1c2530' : '#f3ecf5';
  } catch {
    // An unparseable configured value: leave it exactly as it is.
    return textColor;
  }
};

/** Shade a hex colour toward black (negative) or white (positive). */
const shift = (value: string, amount: number): string => {
  try {
    const c = Color(value);
    return (amount >= 0 ? c.lighten(amount) : c.darken(-amount)).hex();
  } catch {
    return value;
  }
};

const getPalette = (mode: 'light' | 'dark'): PaletteOptions => {
  const isLight = mode === 'light';
  const base = isLight ? appColors.dataBsThemeLight : appColors.dataBsThemeDark;
  const card = isLight ? appColors.dataCardColorLight : appColors.dataCardColorDark;
  const primaryMain = base['--bs-primary'];

  return {
  /**
   * `mode` was never set. Without it MUI treats BOTH themes as light, so
   * `theme.palette.mode` read "light" while dark mode was on, every
   * `mode === 'dark'` branch in the app was dead, and `background.paper`
   * silently defaulted to white — which is why an unstyled `<Paper>`, `<Menu>`
   * or `<Popover>` came out as a white slab on a dark page.
   */
  mode,
  background: {
    default: base['--bs-body-bg'],
    /* Paper follows the configured card colour so MUI-provided surfaces
       (menus, popovers, autocomplete lists) match hand-built cards. */
    paper: card['--bs-card-bg-color'],
  },
  primary: {
    main: primaryMain,
    light: shift(primaryMain, isLight ? 0.5 : 0.25),
    dark: shift(primaryMain, isLight ? -0.22 : -0.18),
    contrastText: isLight ? '#ffffff' : '#07121D',
  },
  /* ------------------------------------------------------------------ *
   * Semantic families
   *
   * Each of these previously declared `main` and nothing else. That looked
   * harmless but quietly disabled the whole soft-tint action system: the
   * table action buttons, status badges and module accents all pick their
   * *text* colour as `family.dark` in light mode and `family.light` in dark,
   * falling back to `main`. With no light/dark defined, every one of those
   * fell back — so an amber "edit" button drew amber text on an amber wash,
   * and the system compensated by staying desaturated everywhere.
   *
   * Deriving them with the same `shift()` the brand colour already uses gives
   * every family a proper three-stop ramp, which is what lets the accents be
   * vivid without becoming unreadable.
   * ------------------------------------------------------------------ */
  secondary: {
    main: base['--bs-secondary'],
    light: shift(base['--bs-secondary'], isLight ? 0.5 : 0.3),
    dark: shift(base['--bs-secondary'], isLight ? -0.25 : -0.18),
    contrastText: '#ffffff',
  },
  success: {
    main: base['--bs-success'],
    light: shift(base['--bs-success'], isLight ? 0.5 : 0.3),
    dark: shift(base['--bs-success'], isLight ? -0.25 : -0.18),
    contrastText: '#ffffff',
  },
  warning: {
    main: base['--bs-warning'],
    light: shift(base['--bs-warning'], isLight ? 0.5 : 0.3),
    dark: shift(base['--bs-warning'], isLight ? -0.3 : -0.2),
    contrastText: '#ffffff',
  },
  error: {
    main: base['--bs-danger'],
    light: shift(base['--bs-danger'], isLight ? 0.5 : 0.3),
    dark: shift(base['--bs-danger'], isLight ? -0.25 : -0.18),
    contrastText: '#ffffff',
  },
  info: {
    main: base['--bs-info'],
    light: shift(base['--bs-info'], isLight ? 0.5 : 0.3),
    dark: shift(base['--bs-info'], isLight ? -0.3 : -0.2),
    contrastText: '#ffffff',
  },
  text: {
    /* Inherited role inversion — see the note in appcolors.ts. `primary` is the
       MUTED body tone here and `secondary` is the STRONG one; the app is
       written against that and renaming the roles would re-tone 195 files. */
    primary: mode === 'light' ? appColors.dataBsThemeLight['--bs-text-color'] : appColors.dataBsThemeDark['--bs-text-color'],
    secondary: mode === 'light' ? appColors.dataBsThemeLight['--bs-secondary-text'] : appColors.dataBsThemeDark['--bs-secondary-text'],
    disabled: isLight ? 'rgba(20, 33, 47, 0.38)' : 'rgba(233, 238, 245, 0.38)',
  },
  divider: mode === 'light' ? appColors.dataBsThemeLight['--bs-divider'] : appColors.dataBsThemeDark['--bs-divider'],
  /* Hover/selected washes are tinted with the brand rather than plain black,
     which is what keeps row and menu hovers looking intentional. */
  action: {
    hover: isLight ? 'rgba(15, 92, 171, 0.05)' : 'rgba(255, 255, 255, 0.06)',
    hoverOpacity: isLight ? 0.05 : 0.06,
    selected: isLight ? 'rgba(15, 92, 171, 0.09)' : 'rgba(63, 169, 214, 0.14)',
    selectedOpacity: isLight ? 0.09 : 0.14,
    focus: isLight ? 'rgba(15, 92, 171, 0.12)' : 'rgba(63, 169, 214, 0.18)',
  },



  // Add custom topbar colors
  topbarButton: {
    topbarBg: mode === 'light' ? appColors.dataTopbarColorLight['--bs-topbar-bg'] : appColors.dataTopbarColorDark['--bs-topbar-bg'],
    topbarItemColor: mode === 'light' ? appColors.dataTopbarColorLight['--bs-topbar-item-color'] : appColors.dataTopbarColorDark['--bs-topbar-item-color'],
    topbarSearchBg: mode === 'light' ? appColors.dataTopbarColorLight['--bs-topbar-search-bg'] : appColors.dataTopbarColorDark['--bs-topbar-search-bg'],
    drawerHeader: mode === 'light' ? appColors.dataTopbarColorLight['--bs-drawer-header-color'] : appColors.dataTopbarColorDark['--bs-drawer-header-color'],
    drawer: mode === 'light' ? appColors.dataTopbarColorLight['--bs-drawer-color'] : appColors.dataTopbarColorDark['--bs-drawer-color'],
    topBarHeading: mode === 'light' ? appColors.dataTopbarColorLight['topBarHeading'] : appColors.dataTopbarColorDark['topBarHeading'],
    drawerHeadingText: legibleOn(
      mode === 'light' ? appColors.dataTopbarColorLight['drawerHeadingText'] : appColors.dataTopbarColorDark['drawerHeadingText'],
      mode === 'light' ? appColors.dataMenuColorLight['--bs-main-nav-bg'] : appColors.dataBssThemeDarkMenuColorLight['--bs-main-nav-bg'],
    ),
    drawerMenuText: legibleOn(
      mode === 'light' ? appColors.dataTopbarColorLight['drawerMenuText'] : appColors.dataTopbarColorDark['drawerMenuText'],
      mode === 'light' ? appColors.dataMenuColorLight['--bs-main-nav-bg'] : appColors.dataBssThemeDarkMenuColorLight['--bs-main-nav-bg'],
    ),
  },
  mainMenuNav: {
    mainNavBg: mode === 'light' ? appColors.dataMenuColorLight['--bs-main-nav-bg'] : appColors.dataBssThemeDarkMenuColorLight['--bs-main-nav-bg'],
    hoverColorNavBg: mode === 'light' ? appColors.dataBssThemeDarkMenuColorLight['--bs-main-nav-item-hover-color'] : appColors.dataBssThemeDarkMenuColorLight['--bs-main-nav-item-hover-color'],
  },
  heading: {
    headingColor: mode === 'light' ? appColors.dataTopbarColorLight['--bs-heading-color'] : appColors.dataTopbarColorDark['--bs-heading-color'],
  },
  cardAdd: {
    headingColor: mode === 'light' ? appColors.dataCardColorLight['--bs-heading-color'] : appColors.dataCardColorDark['--bs-heading-color'],
    bgColor: mode === 'light' ? appColors.dataCardColorLight['--bs-card-bg-color'] : appColors.dataCardColorDark['--bs-card-bg-color'],
    tablehover: mode === 'light' ? appColors.dataCardColorLight['--bs-table-hover-color'] : appColors.dataCardColorDark['--bs-table-hover-color'],
    borderColor: mode === 'light' ? appColors.dataCardColorLight['--bs-input-border-color'] : appColors.dataCardColorDark['--bs-input-border-color'],
    textDescription: mode === 'light' ? appColors.dataBsThemeLight['--bs-body-bg'] : appColors.dataCardColorDark['--bs-card-bg-color'],
    rowHeaderColor: mode === 'light' ? appColors.dataCardColorLight['--bs-row-header-color'] : appColors.dataCardColorDark['--bs-row-header-color'],
    default: mode === 'light' ? appColors.dataBsThemeLight['--bs-body-defult'] : appColors.dataBsThemeDark['--bs-body-bg'],
    secBackColor: mode === 'light' ? appColors.dataCardColorLight['--bs-card-sec-bg-color'] : appColors.dataCardColorDark['--bs-card-sec-bg-color'],
    thirdBgColor: mode === 'light' ? appColors.dataCardColorLight['thirdBgColor'] : appColors.dataCardColorDark['thirdBgColor'],

  },
  table: {
    headerBgColor: mode === 'light' ? appColors.dataBssThemeDark['--bs-light-rgb'] : appColors.dataBssThemeDark['--bs-dark-rgb'],
    nameHeadingColor: mode === 'light' ? appColors.dataCardColorLight['--bs-table-name-header-color'] : appColors.dataCardColorDark['--bs-table-name-header-color'],
  },
  iconButton: {
    editButtonColor: mode === 'light' ? appColors.iconButtonLight['editButtonColor'] : appColors.iconButtonDark['editButtonColor'],
    editIconColor: mode === 'light' ? appColors.iconButtonLight['editIconColor'] : appColors.iconButtonDark['editIconColor'],
    viewButtonColor: mode === 'light' ? appColors.iconButtonLight['viewButtonColor'] : appColors.iconButtonDark['viewButtonColor'],
    viewIconColor: mode === 'light' ? appColors.iconButtonLight['viewIconColor'] : appColors.iconButtonDark['viewIconColor'],
    deleteButtonColor: mode === 'light' ? appColors.iconButtonLight['deleteButtonColor'] : appColors.iconButtonDark['deleteButtonColor'],
    deleteIconColor: mode === 'light' ? appColors.iconButtonLight['deleteIconColor'] : appColors.iconButtonDark['deleteIconColor'],
    addButtonColor: mode === 'light' ? appColors.iconButtonLight['addButtonColor'] : appColors.iconButtonDark['addButtonColor'],
    addIconColor: mode === 'light' ? appColors.iconButtonLight['addIconColor'] : appColors.iconButtonDark['addIconColor'],
  },
  orderListProp: {
    leftBgColor: mode === 'light' ? appColors.orderListPropLight['leftBgColor'] : appColors.orderListPropDark['leftBgColor'],
    lable1BgColor: mode === 'light' ? appColors.orderListPropLight['lable1BgColor'] : appColors.orderListPropDark['lable1BgColor'],
    lable2BgColor: mode === 'light' ? appColors.orderListPropLight['lable2BgColor'] : appColors.orderListPropDark['lable2BgColor'],
    lable1FontColor: mode === 'light' ? appColors.orderListPropLight['lable1FontColor'] : appColors.orderListPropDark['lable1FontColor'],
    lable2FontColor: mode === 'light' ? appColors.orderListPropLight['lable2FontColor'] : appColors.orderListPropDark['lable2FontColor'],
    lable1buttonIconColor: mode === 'light' ? appColors.orderListPropLight['lable1buttonIconColor'] : appColors.orderListPropDark['lable1buttonIconColor'],
    headingColor: mode === 'light' ? appColors.orderListPropLight['headingColor'] : appColors.orderListPropDark['headingColor'],
    lable2buttonIconColor: mode === 'light' ? appColors.orderListPropLight['lable2buttonIconColor'] : appColors.orderListPropDark['lable2buttonIconColor'],
    lable2buttonbgColor: mode === 'light' ? appColors.orderListPropLight['lable2buttonbgColor'] : appColors.orderListPropDark['lable2buttonbgColor'],
    payButtonColor: mode === 'light' ? appColors.orderListPropLight['payButtonColor'] : appColors.orderListPropDark['payButtonColor'],
    lable1buttonbgColor: mode === 'light' ? appColors.orderListPropLight['lable1buttonbgColor'] : appColors.orderListPropDark['lable1buttonbgColor'],
    rightFontColor: mode === 'light' ? appColors.orderListPropLight['rightFontColor'] : appColors.orderListPropDark['rightFontColor'],
  },
  };
};

// Export palettes for light and dark modes
export const paletteLight: PaletteOptions = getPalette('light');
export const paletteDark: PaletteOptions = getPalette('dark');
